/* global BigInt */
/**
 * Statistics Flags using BigInt for unlimited expansion
 * Each flag represents a specific statistic type that can be requested
 */

// Book Creator Statistics (0-31)
export const BC_BOOKS = 1n;
export const BC_IN_PROGRESS = 2n;
export const BC_COMPLETED = 4n;
export const BC_VOICE_ACTORS = 8n;
export const BC_LIT_GROUPS = 16n;
export const BC_OPPORTUNITIES = 32n;
export const BC_ACTIVE_GROUPS = 64n;
export const BC_GROUP_TYPES = 128n;

// Voice Actor Statistics (256+)
export const VA_ASSIGNMENTS = 256n;
export const VA_COMPLETED_ASSIGNMENTS = 512n;
export const VA_CHARACTERS = 1024n;
export const VA_RECORDINGS = 2048n;
export const VA_SAMPLES = 4096n;
export const VA_APPLICATIONS = 8192n;
export const VA_REVIEWS = 16384n;
export const VA_RATINGS = 32768n;
export const VA_PROFILE_COMPLETE = 65536n;

// Admin/System Statistics (1M+)
export const ADMIN_USERS = 1048576n;
export const ADMIN_BOOKS = 2097152n;
export const ADMIN_VOICE_ACTORS = 4194304n;
export const ADMIN_RECORDINGS = 8388608n;
export const ADMIN_ACTIVE_USERS = 16777216n;
export const ADMIN_BOOK_CREATORS = 33554432n;
export const ADMIN_ADMINISTRATORS = 67108864n;

// Message/Communication Statistics (100M+)
export const MSG_TOTAL = 134217728n;
export const MSG_UNREAD = 268435456n;
export const MSG_APPLICATIONS = 536870912n;

// Audio Construction Statistics (1B+)
export const AUDIO_FILES = 1073741824n;
export const AUDIO_PROCESSED = 2147483648n;
export const AUDIO_IN_PROGRESS = 4294967296n;

/**
 * Combined flags object for easier access
 */
export const STAT_FLAGS = {
  // Book Creator
  BC_BOOKS,
  BC_IN_PROGRESS,
  BC_COMPLETED,
  BC_VOICE_ACTORS,
  BC_LIT_GROUPS,
  BC_OPPORTUNITIES,
  BC_ACTIVE_GROUPS,
  BC_GROUP_TYPES,

  // Voice Actor
  VA_ASSIGNMENTS,
  VA_COMPLETED_ASSIGNMENTS,
  VA_CHARACTERS,
  VA_RECORDINGS,
  VA_SAMPLES,
  VA_APPLICATIONS,
  VA_REVIEWS,
  VA_RATINGS,
  VA_PROFILE_COMPLETE,

  // Admin/System
  ADMIN_USERS,
  ADMIN_BOOKS,
  ADMIN_VOICE_ACTORS,
  ADMIN_RECORDINGS,
  ADMIN_ACTIVE_USERS,
  ADMIN_BOOK_CREATORS,
  ADMIN_ADMINISTRATORS,

  // Messages
  MSG_TOTAL,
  MSG_UNREAD,
  MSG_APPLICATIONS,

  // Audio
  AUDIO_FILES,
  AUDIO_PROCESSED,
  AUDIO_IN_PROGRESS
};

/**
 * Predefined statistics sets for common views
 */
export const STAT_SETS = {
  // Your Portfolio (my books, in progress, completed, voice actors)
  BOOK_CREATOR_PORTFOLIO:
    BC_BOOKS | BC_IN_PROGRESS | BC_COMPLETED | BC_VOICE_ACTORS,

  // Library Overview (literary groups, total books, in progress, completed)
  LIBRARY_OVERVIEW:
    BC_LIT_GROUPS | BC_BOOKS | BC_IN_PROGRESS | BC_COMPLETED,

  // Your Book Portfolio (literary groups, total books, in progress, completed)
  BOOK_PORTFOLIO:
    BC_LIT_GROUPS | BC_BOOKS | BC_IN_PROGRESS | BC_COMPLETED,

  // Opportunity Portfolio (literary groups, total opportunities, open opportunities, active assignments)
  OPPORTUNITY_PORTFOLIO:
    BC_LIT_GROUPS | BC_OPPORTUNITIES | VA_ASSIGNMENTS,

  // Your Literary Portfolio (literary groups, total books, active groups, group types)
  LITERARY_PORTFOLIO:
    BC_LIT_GROUPS | BC_BOOKS | BC_ACTIVE_GROUPS | BC_GROUP_TYPES,

  // System Overview (users, books, voice actors, recordings)
  SYSTEM_OVERVIEW:
    ADMIN_USERS | ADMIN_BOOKS | ADMIN_VOICE_ACTORS | ADMIN_RECORDINGS,

  // User Statistics (total users, voice actors, book creators, administrators, active users)
  USER_STATISTICS:
    ADMIN_USERS | ADMIN_VOICE_ACTORS | ADMIN_BOOK_CREATORS |
    ADMIN_ADMINISTRATORS | ADMIN_ACTIVE_USERS,

  // Your Performance (assignments, completed, characters, recordings)
  VOICE_ACTOR_PERFORMANCE:
    VA_ASSIGNMENTS | VA_COMPLETED_ASSIGNMENTS | VA_CHARACTERS | VA_RECORDINGS,

  // Portfolio Overview - voice actor (voice samples, active applications, reviews, average ratings, profile complete)
  VOICE_ACTOR_PORTFOLIO:
    VA_SAMPLES | VA_APPLICATIONS | VA_REVIEWS | VA_RATINGS | VA_PROFILE_COMPLETE,

  // Dashboard views by role
  ADMIN_DASHBOARD:
    ADMIN_USERS | ADMIN_BOOKS | ADMIN_VOICE_ACTORS | ADMIN_ACTIVE_USERS,

  BOOK_CREATOR_DASHBOARD:
    BC_BOOKS | BC_IN_PROGRESS | BC_OPPORTUNITIES | BC_LIT_GROUPS,

  VOICE_ACTOR_DASHBOARD:
    VA_ASSIGNMENTS | VA_RECORDINGS | VA_APPLICATIONS | VA_SAMPLES
};

/**
 * Helper function to check if a flag is set
 */
export const hasFlag = (flags, flag) => {
  return (BigInt(flags) & BigInt(flag)) !== 0n;
};

/**
 * Helper function to combine multiple flags
 */
export const combineFlags = (...flags) => {
  return flags.reduce((acc, flag) => acc | BigInt(flag), 0n);
};

/**
 * Helper function to get flag names from a combined flag value
 */
export const getFlagNames = (combinedFlags) => {
  const names = [];
  const flagValue = BigInt(combinedFlags);

  for (const [name, value] of Object.entries(STAT_FLAGS)) {
    if ((flagValue & value) !== 0n) {
      names.push(name);
    }
  }

  return names;
};
